<template>
  <div v-loading="loading" class="meeting-card">
    <el-form
      ref="formRef"
      v-loading="loading"
      class="record-upload-auto-form"
      label-position="top"
      :model="formData"
      require-asterisk-position="right"
      :rules="formRules"
      style="max-width: 600px"
      :validate-on-rule-change="false"
    >
      <!-- 本次跟进对象选择 -->
      <el-form-item v-if="showCustomerField" class="clue-select">
        <template v-if="isVisit">
          <div class="label">客户</div>
        </template>
        <template v-else>
          <el-select
            v-model="followUpObject"
            :disabled="allDisabled"
            placeholder="请选择"
            style="width: 80px; flex: 0 0 auto"
          >
            <el-option
              v-for="item in followUpObjectList"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </template>
        <!-- 线索选择 -->
        <template v-if="showClue && !isVisit">
          <MobileClueSelect
            v-model="formData.customer"
            :disabled="allDisabled"
            @change="handleFollowUpClueChange"
          >
            <template #trigger="{ value }">
              <el-select
                class="mobile-clue-select-trigger"
                :disabled="allDisabled"
                :model-value="value?.id"
                placeholder="请选择线索"
              >
                <el-option v-if="value?.id" :key="value.id" :label="value.name" :value="value.id" />
              </el-select>
            </template>
          </MobileClueSelect>
        </template>
        <template v-else-if="showClue && isVisit">
          <!-- 日程助手 只能上传客户，若是线索选择，客户不可用，提示用户联系管理员 -->
          <div class="upload-tips">
            <i class="icon-error-warning-line t-iconfont"></i>
            <span>未配置跟进客户入库，请联系管理员</span>
          </div>
        </template>
        <!-- 客户选择 -->
        <template v-else>
          <CustomerSelectLocation
            ref="customerSelectRef"
            v-model="formData.customer"
            :add-customer-show="true"
            :disabled="allDisabled || isVisit"
            :is-location="isLocation && !inxbb"
            @addCustomer="handleAddCustomer"
            @change="resetValidate"
          />
        </template>
      </el-form-item>
      <!-- 分析模板 -->
      <el-form-item label="分析模版" prop="ruleId">
        <el-select
          v-model="formData.ruleId"
          :disabled="isAnalysisTemplateDisabled || allDisabled"
          placeholder="请选择"
          @change="handleRuleChange"
        >
          <el-option
            v-for="option in analysisTemplateList"
            :key="option.value"
            :label="option.label"
            :value="option.value"
          />
        </el-select>
      </el-form-item>

      <!-- 销售机会 -->
      <el-form-item
        v-if="isAuto && showOpportunityField"
        label="销售机会"
        prop="salesOpportunityId"
      >
        <el-select
          v-model="formData.salesOpportunityId"
          :disabled="isOpportunityDisabled || allDisabled"
          placeholder="请选择"
          @change="handleSalesOpportunityChange"
        >
          <el-option
            v-for="option in salesOpportunityList"
            :key="option.id"
            :label="option.name"
            :value="option.id"
          />
        </el-select>
      </el-form-item>

      <!-- 跟进方式 -->
      <el-form-item
        v-if="isAuto && showFollowUpMethodField"
        label="跟进方式"
        prop="followUpMethodId"
      >
        <el-select
          v-model="formData.followUpMethodId"
          :disabled="allDisabled"
          placeholder="请选择"
          @change="handleFollowUpMethodChange"
        >
          <el-option
            v-for="option in followUpMethodList"
            :key="option.id"
            :label="option.name"
            :value="option.id"
          />
        </el-select>
      </el-form-item>
    </el-form>

    <AiUseXbbForm
      v-if="xbbUrl && showXbb"
      v-model:loading="iframeLoading"
      v-model:show-xbb="showXbb"
      :xbb-url="xbbUrl"
      @handleXbbSuccess="handleXbbSuccess"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import AppMessage from '@/components/message/index.js'
import {
  getSummaryRuleList,
  getSalesOpportunityList,
  getCustomRecordInfo,
  addCustomerCommunicate,
  getCommunicateTypeList
} from '@/api'
// import LoadingCard from '@/components/Cards/LoadingCard.vue'
// import CustomerSelect from '../CustomerSelect.vue'
import CustomerSelectLocation from '../CustomerSelectLocation.vue'
import MobileClueSelect from '../MobileClueSelect.vue'
import { isMobile } from '@/utils'
import xbbSdk from 'xbb-sdk'
import { useAppStore } from '@/store/useAppStore'
import { BUSINESS_TYPE } from '@/config/config.js'
import AiUseXbbForm from '@/components/ai-use-xbbform/index.vue'

const emit = defineEmits(['scrollEnd', 'changeExtractStatus', 'addMessage', 'refreshList'])
const props = defineProps({
  fileUrl: {
    type: String
  },
  attributes: {
    type: Object,
    default: () => ({})
  },
  isLocation: {
    type: Boolean,
    default: false
  },
  isAuto: {
    type: Boolean,
    default: false
  },
  isVisit: {
    type: Boolean,
    default: false
  },
  visitItem: {
    type: Object,
    default: () => ({})
  }
})

// 基础状态
const loading = ref(false)
const formRef = ref()
const allDisabled = ref(false)
const meetingRuleShowTag = ref(1)
const showClue = ref(false)
const isInitLoad = ref(true)

// 表单数据
const formData = ref({
  ruleId: undefined,
  customer: undefined,
  salesOpportunityId: undefined,
  followUpMethodId: undefined
})
// 跟进对象
const followUpObject = ref(BUSINESS_TYPE.CUSTOMER)

// 下拉列表数据
const analysisTemplateList = ref([])
const salesOpportunityList = ref([])
const followUpMethodList = ref([])
const followUpObjectList = ref([])
// const customerList = ref([])

// 提交数据缓存
const submitDataCache = ref({})

// 计算属性：控制字段显示状态
const showRuleField = computed(() => !!props.attributes?.rule)
const showCustomerField = computed(() => !!props.attributes?.storageConfigInfo?.followUpObject)
const showOpportunityField = computed(
  () => !!props.attributes?.opportunity?.frontShow && !showClue.value
)
const showFollowUpMethodField = computed(() => !!props.attributes?.communicateTypeInfo?.frontShow)

// 计算属性：控制字段禁用状态
const isAnalysisTemplateDisabled = computed(() => analysisTemplateList.value.length === 1)
const isOpportunityDisabled = computed(() => !formData.value.customer?.id)

// 计算属性：表单校验规则
const formRules = computed(() => {
  const rules = {}

  // 分析模板规则
  // if (showRuleField.value && props.attributes?.rule?.required) {
  rules.ruleId = [{ required: true, message: '该字段为必填不能为空', trigger: 'change' }]
  // }

  // 客户选择规则
  if (showCustomerField.value && props.attributes?.customer?.required) {
    rules['customer.id'] = [{ required: true, message: '该字段为必填不能为空', trigger: 'change' }]
  }

  // 销售机会规则
  if (
    showOpportunityField.value &&
    props.attributes?.opportunity?.required &&
    salesOpportunityList.value.length
  ) {
    rules.salesOpportunityId = [
      { required: true, message: '该字段为必填不能为空', trigger: 'change' }
    ]
  }

  // 跟进方式规则
  if (showFollowUpMethodField.value && props.attributes?.communicateTypeInfo?.required) {
    rules.followUpMethodId = [
      { required: true, message: '该字段为必填不能为空', trigger: 'change' }
    ]
  }

  return rules
})

/**
 * 规范化跟进对象值
 * @param {number|string} val - 跟进对象值
 * @returns {number} 规范化后的跟进对象值
 */
const normalizeFollowUpObject = (val) => {
  const num = +val
  if (Number.isNaN(num)) return BUSINESS_TYPE.CUSTOMER
  if (num === BUSINESS_TYPE.SPECIAL) return BUSINESS_TYPE.CUSTOMER
  return num
}

/**
 * 初始化规则字段
 * @param {Object} attributes - 属性对象
 */
const initRuleField = (attributes) => {
  if (attributes.rule?.id) {
    formData.value.ruleId = attributes.rule.id || attributes.rule.value
    analysisTemplateList.value = [
      {
        label: attributes.rule?.name || '--',
        value: formData.value?.ruleId
      }
    ]
  }
}

/**
 * 初始化跟进对象字段
 * @param {Object} attributes - 属性对象
 */
const initFollowUpObjectField = (attributes) => {
  const _storageConfigInfo = attributes.storageConfigInfo
  if (_storageConfigInfo) {
    const _followUpObject = normalizeFollowUpObject(_storageConfigInfo?.followUpObject)
    followUpObject.value = _followUpObject

    const businessTypes = _storageConfigInfo.businessTypes
    if (businessTypes) {
      followUpObjectList.value = businessTypes.map((item) => ({
        value: item,
        label: item === BUSINESS_TYPE.CUSTOMER ? '客户' : '线索'
      }))
    } else {
      followUpObjectList.value = [
        {
          value: BUSINESS_TYPE.CUSTOMER,
          label: '客户'
        }
      ]
    }
  }

  // // 历史数据兼容
  // if (attributes.businessType) {
  //   const businessType = normalizeFollowUpObject(attributes.businessType)
  //   followUpObject.value = businessType
  //   followUpObjectList.value = [
  //     {
  //       value: businessType,
  //       label: businessType === BUSINESS_TYPE.CUSTOMER ? '客户' : '线索'
  //     }
  //   ]
  //   if (businessType === BUSINESS_TYPE.CLUE) {
  //     formData.value.customer = attributes.customer?.id
  //   }
  // }
}

/**
 * 初始化客户字段
 * @param {Object} attributes - 属性对象
 */
// const initCustomerField = (attributes) => {
// 是否为当前操作的数据
// const isCurrentOperationData =
//   +attributes?.storeTag === 0 && followUpObject.value === BUSINESS_TYPE.CUSTOMER

// if (isCurrentOperationData) {
//   // isCustomerHistory.value = false
// }
//  else if (
//   !!+attributes?.storeTag &&
//   attributes.businessType === BUSINESS_TYPE.CUSTOMER &&
//   attributes?.customer?.id
// ) {
//   formData.value.customer = {
//     id: attributes.customer?.id,
//     name: attributes.customer?.name
//   }
//   customerList.value = [formData.value.customer]
// }
// }

/**
 * 初始化销售机会字段
 * @param {Object} attributes - 属性对象
 */
// const initOpportunityField = (attributes) => {
//   if (!attributes.opportunity) return
//   const oppId = attributes.opportunity?.id
//   salesOpportunityList.value = [
//     {
//       id: oppId,
//       name: attributes.opportunity?.name
//     }
//   ]
//   // 使用 nextTick 确保选项已挂载后再赋值
//   nextTick(() => {
//     formData.value.salesOpportunityId = oppId
//   })
// }

/**
 * 初始化跟进方式字段
 * @param {Object} attributes - 属性对象
 */
// const initFollowUpMethodField = (attributes) => {
//   const communicateTypeInfo = attributes.communicateTypeInfo
//   if (!communicateTypeInfo?.value) return

//   if (attributes.rule?.id) {
//     // 如果已有规则ID，使用规则中的跟进方式信息
//     formData.value.followUpMethodId = communicateTypeInfo.value
//     followUpMethodList.value = [
//       {
//         name: communicateTypeInfo?.text || '--',
//         id: communicateTypeInfo.value
//       }
//     ]
//   } else {
//     // 否则使用已加载的跟进方式列表
//     formData.value.followUpMethodId = communicateTypeInfo.value
//   }
// }

/**
 * 初始化禁用状态
 * @param {Object} attributes - 属性对象
 */
const initDisabledState = (attributes) => {
  if (attributes.storeTag === 1) {
    allDisabled.value = true
  }
}

/**
 * 初始化表单数据
 * 统一管理所有初始化逻辑：先并行拉取所需数据，再顺序初始化各字段
 */
const initFormData = async () => {
  const attributes = props.attributes
  console.log('---VoiceCardAuto attributes: ', attributes)

  // 第一步：并行拉取所需的数据列表
  const fetchTasks = []
  if (!attributes.rule?.id) {
    // 如果没有规则ID，需要拉取规则列表和跟进方式列表
    fetchTasks.push(loadRulesList())
    fetchTasks.push(getTheCommunicateTypeList())
  }
  if (fetchTasks.length) {
    await Promise.all(fetchTasks)
  }

  // 第二步：按顺序初始化各字段（有依赖关系）
  initRuleField(attributes)
  initFollowUpObjectField(attributes)
  // initCustomerField(attributes)
  // initOpportunityField(attributes)
  // initFollowUpMethodField(attributes) // 跟进方式初始化，暂时注释，好像不用选中
  initDisabledState(attributes)

  // 第三步：初始化提交数据缓存
  updateSubmitDataCache()
}
/**
 * 处理入库标签变更
 * 更新 attributes 的 storeTag 和 customer 信息，并触发滚动到底部事件
 */
const handleChangeStoreTag = () => {
  // eslint-disable-next-line vue/no-mutating-props
  props.attributes.storeTag = 1
  // eslint-disable-next-line vue/no-mutating-props
  props.attributes.customer = {}
  // eslint-disable-next-line vue/no-mutating-props
  props.attributes.customer.customerId = formData.value.customer
  // eslint-disable-next-line vue/no-mutating-props
  props.attributes.customer.customerName = formData.value.customer
  emit('scrollEnd')
}

/**
 * 处理 XBB iframe 成功消息（移动端）
 * 根据业务类型处理客户沟通记录或客户创建成功
 * @param {Object} e - 消息事件对象
 */
const handleXbbSuccess = (e) => {
  useAppStore().setIsXbbIframeShow(false)
  showXbb.value = false
  const params = e.data.params
  if (+params.businessType === BUSINESS_TYPE.CUSTOMER_COMMUNICATE) {
    addCustomerCommunicate({
      customerCommunicateId: e.data.dataId,
      // eslint-disable-next-line no-undef
      customerId: customer.value.id,
      chatRecordId: props.attributes.id
    }).then(async () => {
      handleChangeStoreTag()
      emit('refreshList')
    })
  } else if (+params.businessType === BUSINESS_TYPE.CUSTOMER) {
    formData.value.customer = {
      id: e.data.dataId,
      name: params.dataList.text_1
    }
    customerSelectRef.value.getList()
  }
}

const xbbIframeWeb = (e) => {
  if (e.data?.type === 'aiSuccessWeb') {
    handleXbbSuccessWeb(e)
  }
}

/**
 * 处理 XBB iframe 成功消息（Web端）
 * 校验是否为当前表单，并根据业务类型处理客户沟通记录或客户创建成功
 * @param {Object} e - 消息事件对象
 */
const handleXbbSuccessWeb = (e) => {
  const params = e.data.data
  const result = e.data.result
  const isSameForm =
    params.appId === sendXbbParams.value.appId &&
    params.menuId === sendXbbParams.value.menuId &&
    params.formId === sendXbbParams.value.formId &&
    params.businessType === sendXbbParams.value.businessType
  if (!isSameForm) return
  if (+params.businessType === BUSINESS_TYPE.CUSTOMER_COMMUNICATE) {
    addCustomerCommunicate({
      customerCommunicateId: result.dataId,
      // eslint-disable-next-line no-undef
      customerId: customer.value.id,
      chatRecordId: props.attributes.id
    }).then(async () => {
      handleChangeStoreTag()
      emit('refreshList')
    })
  } else if (+params.businessType === BUSINESS_TYPE.CUSTOMER) {
    customerSelectRef.value.getList()
    formData.value.customer = {
      id: result.dataId,
      name: params.dataList.text_1
    }
  }
}

const FIELD_MAP = [
  {
    key: 'rule',
    field: 'ruleId',
    list: () => analysisTemplateList.value,
    show: () => showRuleField.value,
    required: () => props.attributes?.rule?.required
  },
  {
    key: 'opportunity',
    field: 'salesOpportunityId',
    list: () => salesOpportunityList.value,
    show: () => showOpportunityField.value,
    required: () => props.attributes?.opportunity?.required
  },
  {
    key: 'communicateTypeInfo',
    field: 'followUpMethodId',
    list: () => followUpMethodList.value,
    show: () => showFollowUpMethodField.value,
    required: () => props.attributes?.communicateTypeInfo?.required
  }
]

/**
 * 更新提交数据缓存
 * 根据表单数据和字段配置，构建提交时所需的数据结构
 */
const updateSubmitDataCache = () => {
  submitDataCache.value = {}

  FIELD_MAP.forEach((item) => {
    if (!item.show()) return
    const value = formData.value[item.field]
    if (!value) {
      submitDataCache.value[item.key] = {}
      return
    }
    const option = item.list().find((o) => o.id === value || o.value === value)
    submitDataCache.value[item.key] = {
      id: option?.id ?? value,
      name: option?.name || option?.label,
      required: item.required()
    }
  })

  if (showCustomerField.value) {
    if (formData.value.customer) {
      submitDataCache.value.customer = {
        ...formData.value.customer,
        required: true
      }
    } else {
      submitDataCache.value.customer = {}
    }
  }

  if (+followUpObject.value === BUSINESS_TYPE.CLUE) {
    if (formData.value.customer) {
      submitDataCache.value.customer = {
        id: formData.value.customer.id,
        name: formData.value.customer.name,
        required: true
      }
    } else {
      submitDataCache.value.customer = {}
    }
  }
}
/**
 * 获取跟进方式列表
 * 从接口获取跟进方式列表并转换为组件所需的数据格式
 */
const getTheCommunicateTypeList = async () => {
  const { result } = await getCommunicateTypeList({})
  const tempList = []
  // 遍历跟进方式列表，添加required字段
  result?.communicateTypeList?.forEach((item) => {
    tempList.push({
      id: item.value,
      name: item.text
    })
  })
  followUpMethodList.value = tempList ?? []
}
/**
 * 获取销售机会列表
 * 根据当前选择的客户ID获取该客户的销售机会列表
 */
const fetchSalesOpportunityList = async () => {
  // 销售机会仅适用于客户，线索对象不能使用线索 ID 查询客户机会
  if (+followUpObject.value !== BUSINESS_TYPE.CUSTOMER || !formData.value.customer?.id) return

  try {
    const { result } = await getSalesOpportunityList({
      nameLike: '',
      customerId: formData.value.customer.id
    })
    salesOpportunityList.value = (result?.opportunityList || []).map((it) => ({
      id: it.id,
      name: it.name
    }))
  } catch (error) {
    console.error('获取销售机会列表失败:', error)
  }
}
/**
 * 处理分析模板变更
 * 更新提交数据缓存中的规则信息
 * @param {number|string} value - 选中的规则ID
 */
const handleRuleChange = (value) => {
  const ruleOption = analysisTemplateList.value.find((option) => option.value === value)
  submitDataCache.value.rule = {
    id: value,
    name: ruleOption?.label,
    required: props.attributes?.rule?.required
  }
}
/**
 * 处理销售机会变更
 * 更新提交数据缓存中的销售机会信息
 * @param {number|string} value - 选中的销售机会ID
 */
const handleSalesOpportunityChange = (value) => {
  const opportunityOption = salesOpportunityList.value.find((option) => option.id === value)
  submitDataCache.value.opportunity = {
    id: value,
    name: opportunityOption?.name,
    required: props.attributes?.opportunity?.required
  }
}
/**
 * 处理跟进方式变更
 * 更新提交数据缓存中的跟进方式信息
 * @param {number|string} value - 选中的跟进方式ID
 */
const handleFollowUpMethodChange = (value) => {
  const followUpMethodOption = followUpMethodList.value.find((option) => option.id === value)
  submitDataCache.value.communicateTypeInfo = {
    id: value,
    name: followUpMethodOption?.name,
    required: props.attributes?.communicateTypeInfo?.required
  }
}
/**
 * 处理线索变更
 * @param value 所选值
 */
const handleFollowUpClueChange = (value) => {
  submitDataCache.value.customer = {
    id: value.id,
    name: value.name,
    required: true
  }
}
/**
 * 重置客户字段的验证状态
 * 当客户选择发生变化时，重新验证客户字段
 */
const resetValidate = () => {
  if (formData.value.customer?.id) {
    formRef.value?.validateField('customer.id')
  }
}
/**
 * 构建提交数据
 * 更新缓存并处理特殊字段格式（如跟进方式的 text 和 value 字段）
 * @returns {Object} 处理后的提交数据
 */
// const buildSubmitData = () => {
//   updateSubmitDataCache()
//   const submitData = { ...submitDataCache.value, businessType: followUpObject.value }
//   if (submitData.communicateTypeInfo) {
//     submitData.communicateTypeInfo.text = submitData.communicateTypeInfo.name
//     submitData.communicateTypeInfo.value = submitData.communicateTypeInfo.id
//     delete submitData.communicateTypeInfo.name
//     delete submitData.communicateTypeInfo.id
//   }
//   return submitData
// }

/**
 * 提交表单
 * 验证表单数据，通过后构建提交数据并创建任务
 */
const handleSubmit = () => {
  return new Promise((resolve, reject) => {
    if (showCustomerField.value) {
      const isValidCustomer = showClue.value
        ? !!formData.value?.customer
        : !!formData.value?.customer?.id
      if (!isValidCustomer) {
        AppMessage.error('本次跟进对象未选择')
        resolve(false)
      }
    }
    if (!props.isAuto) resolve(true)
    formRef.value.validate((valid) => {
      console.log('❎❎', followUpObject)
      if (valid) {
        resolve(true)
      } else {
        resolve(false)
      }
    })
  })
}
/**
 * 创建任务并处理结果
 * 调用创建任务接口，成功后更新表单状态、发送事件并获取SSE消息
 * @param {Object} submitData - 提交的数据
 */
// const createTaskAndHandleResult = async (submitData) => {
//   emit('scrollEnd')

//   const params = {
//     recordId: props.attributes?.recordId,
//     fileUrl: props.attributes?.fileUrl || props.fileUrl,
//     messageSourceType: 2,
//     chatTemplateTag: props.attributes?.chatTemplateTag,
//     chatRecordId: props.attributes?.chatRecordId,
//     meetingRuleShowTag: meetingRuleShowTag.value,
//     chatInfo: {
//       id: formData.value.ruleId,
//       name: analysisTemplateList.value.find((option) => option.value === formData.value.ruleId)
//         ?.label,
//       ...submitData
//     }
//   }
//   try {
//     const res = await createTask(params)
//     // 将线索选择由原始 id 规范为对象，避免禁用态切换到 v-model="formData.customer.id" 时值丢失
//     if (
//       +followUpObject.value === BUSINESS_TYPE.CLUE &&
//       formData.value.customer &&
//       typeof formData.value.customer !== 'object'
//     ) {
//       const clueOption = clueList.value.find((option) => +option.id === +formData.value.customer)
//       formData.value.customer = {
//         id: formData.value.customer,
//         name: clueOption?.name
//       }
//     }
//     allDisabled.value = true

//     // 发送成功事件
//     emit('changeExtractStatus', 1)
//     emit('addMessage', {
//       type: 'component',
//       id: res.result?.taskId,
//       extractStatus: 1,
//       componentName: 'LoadingCard',
//       role: 'assistant',
//       content: {
//         component: LoadingCard,
//         attributes: {
//           assistantId: 2,
//           isMedicalIndustry: props.attributes.isMedicalIndustry
//         }
//       }
//     })

//     // 获取SSE消息
//     try {
//       await getSseMsg({ chatRecordId: res.result.chatRecordId })
//     } catch (err) {
//       console.error('获取SSE消息失败:', err)
//     } finally {
//       emit('refreshList', res.result.chatRecordId)
//     }
//   } catch (err) {
//     console.error('创建任务失败:', err)
//     formData.value.ruleId = ''
//     allDisabled.value = false
//   }
// }
/**
 * 加载分析模板列表
 * 获取规则列表，如果只有一个规则则自动选中，根据配置决定是否自动提交
 */
const loadRulesList = async () => {
  // try {
  const { result } = await getSummaryRuleList()
  analysisTemplateList.value = result.dataList.map((item) => ({
    value: item.id,
    label: item.name
  }))

  meetingRuleShowTag.value = result.meetingRuleShowTag

  // 如果只有一个规则，默认选中并禁用
  if (analysisTemplateList.value.length === 1) {
    formData.value.ruleId = analysisTemplateList.value[0].value
    handleRuleChange(formData.value.ruleId)
  }

  // } catch (error) {
  //   console.error('加载分析模板列表失败:', error)
  // }
}
//新建客户
const inxbb = ref(false)
const customerSelectRef = ref(null)
const xbbUrl = ref('')
const showXbb = ref(false)
const iframeLoading = ref(false)
const sendXbbParams = ref({})
/**
 * 初始化环境配置
 * 从 URL 参数中获取 inxbb 参数，判断是否在销帮帮环境中
 */
const environmentInit = () => {
  // 获取完整 URL 中的搜索参数部分（即 "?inxbb=1"）
  const searchParams = new URLSearchParams(window.location.search)
  // 通过参数名 "inxbb" 获取对应的值
  const inxbbValue = searchParams.get('inxbb') || ''
  inxbb.value = +inxbbValue === 1
}
/**
 * 获取自定义记录参数
 * 根据业务类型获取表单相关的参数信息
 * @param {number} type - 业务类型
 * @returns {Promise<Object>} 包含 appId、menuId、formId 等参数的对象
 */
const getParams = async (type) => {
  const res = await getCustomRecordInfo({ businessType: type })
  const { appId, menuId, formId, businessType, subBusinessType } = res.result
  return { appId, menuId, formId, businessType, subBusinessType }
}
/**
 * 获取销帮帮表单URL
 * 根据业务类型和是否使用AI位置生成表单URL
 * @param {Object} params - 参数对象
 * @param {number} params.type - 业务类型
 * @param {number} params.useAiPosition - 是否使用AI位置, 不为1则使用销帮帮新建自带的定位
 * @returns {Promise<string>} 表单URL
 */
async function getUrl({ type, useAiPosition }) {
  // const frontUrl = 'https://saetestdingtalkf.xbongbong.com.cn/pro/index.html'
  // const frontUrl = 'http://192.168.21.198:8080'
  const path = 'application/newForm'
  const aiPosition = useAiPosition === 1 ? '&useAiPosition=1' : ''
  const res = await getCustomRecordInfo({ businessType: type })
  const { appId, menuId, formId, businessType, subBusinessType, frontUrl } = res.result
  return `${frontUrl}?isAi=1${aiPosition}#/${path}?appId=${appId}&menuId=${menuId}&formId=${formId}&saasMark=1&distributorMark=0&businessType=${businessType}&subBusinessType=${subBusinessType}&timestamp=${new Date().getTime()}&isAiRoot=1`
}
/**
 * 处理新建客户操作
 * 根据环境（移动端/PC端、是否在销帮帮中）选择不同的新建方式
 */
const handleAddCustomer = async () => {
  if (isMobile() || !inxbb.value) {
    console.log('调用移动端新建')
    xbbUrl.value = await getUrl({
      type: 100,
      useAiPosition: 1
    })
    iframeLoading.value = true
    if (!isMobile()) {
      customerSelectRef.value.dropdownRef.handleClose()
    }
    useAppStore().setIsXbbIframeShow(true)
    showXbb.value = true
  } else {
    const params = await getParams(100)
    sendXbbParams.value = {
      appId: params.appId,
      menuId: params.menuId,
      formId: params.formId,
      saasMark: 1,
      businessType: params.businessType,
      subBusinessType: params.subBusinessType
    }
    xbbSdk.sendMessage('addNewForm', {
      appId: params.appId,
      menuId: params.menuId,
      formId: params.formId,
      saasMark: 1,
      businessType: params.businessType,
      subBusinessType: params.subBusinessType
    })

    if (!isMobile()) {
      customerSelectRef.value.dropdownRef.handleClose()
    }
  }
}
// 监听客户变化
watch(
  () => formData.value.customer,
  (newCustomer) => {
    // 清空销售机会选择
    formData.value.salesOpportunityId = undefined

    // 是否为当前操作的数据
    // const isCurrentOperationData =
    // isNaN(+props.attributes?.businessType) && followUpObject.value === BUSINESS_TYPE.CUSTOMER

    console.log('?????????????', newCustomer)

    // // 重新获取销售机会列表
    // if (isCurrentOperationData && newCustomer?.id) {
    fetchSalesOpportunityList()
    // }
  },
  { deep: true }
)
// 监听销售机会列表变化
watch(
  () => props.attributes?.salesOpportunityList,
  (newList) => {
    if (!newList) return
    // 归一化 id 为字符串
    salesOpportunityList.value = (newList || []).map((it) => ({
      id: it.id,
      name: it.name
    }))

    // 如果当前 formData 的 salesOpportunityId 与 option 类型不一致，重置一次以触发显示 label
    nextTick(() => {
      const cur = formData.value.salesOpportunityId
      if (cur != null) {
        const curStr = cur
        const found = salesOpportunityList.value.find((o) => o.id === curStr)
        if (found) {
          // 重新赋值为字符串，确保 el-select 能匹配到 option 并显示 label
          formData.value.salesOpportunityId = found.id
        }
      }
    })
  },
  { deep: true }
)
// 监听followUpObject变化，更新showClue
watch(
  () => followUpObject.value,
  (newVal, oldVal) => {
    // 仅在实际业务类型变更时清空已选客户/线索，避免相同值但不同类型（如数字/字符串）触发误清空
    if (!isInitLoad.value && +newVal !== +oldVal) {
      formData.value.customer = undefined
    }

    if (+newVal === BUSINESS_TYPE.CLUE) {
      showClue.value = true
    } else {
      showClue.value = false
    }
  }
)

// 组件挂载时初始化
onMounted(async () => {
  loading.value = true
  try {
    environmentInit()

    // 初始化表单数据（内部已处理线索列表的历史数据判断）
    await initFormData()

    // 如果有id，则禁用表单
    // if (props.attributes?.id) {
    //   formData.value.ruleId = props.attributes.id
    //   allDisabled.value = true
    // }
  } finally {
    loading.value = false
  }
  nextTick(() => {
    isInitLoad.value = false
  })
  window.addEventListener('message', xbbIframeWeb)
  if (props.isVisit) {
    formData.value.customer = {
      id: props.visitItem.customerId,
      name: props.visitItem.customerName
    }
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('message', xbbIframeWeb)
})

defineExpose({ formData, submitDataCache, followUpObject, handleSubmit, showClue })
</script>

<style lang="less" scoped>
.text-color-main {
  color: #323233;
}
.text-color-normal {
  color: #969799;
}

.clue-select {
  background: #f7f8fa;
  // :deep(.el-form-item__label::after) {
  //   content: '*';
  //   color: red;
  //   margin-left: 4px;
  // }
  :deep(.el-form-item__content) {
    display: flex;
    flex-wrap: nowrap;
  }
  .label {
    margin: 0 12px;
    width: 56px;
    color: #646566;
  }
  .mobile-clue-select-trigger {
    width: 100%;
    pointer-events: none;
  }
  .upload-tips {
    display: flex;
    color: #ffb521;
    align-items: center;
    i {
      margin-right: 4px;
      font-size: 16px;
    }
    span {
      font-size: 14px;
      line-height: 20px;
    }
  }
}

.tips {
  display: flex;
  align-items: center;
  margin-bottom: 18px;

  &-text {
    font-family: PingFang SC;
    font-size: 14px;
    font-weight: normal;
    line-height: normal;
    letter-spacing: 0em;
    color: #969799;
    margin-left: 8px;
  }
}

.btn {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.meeting-card {
  width: 100%;
  max-width: 400px;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans',
    'Helvetica Neue', sans-serif;
  color: #333;
  border-radius: 8px;
  background: #fff;
  padding: 12px 0 0 0;

  .card-header {
    margin-bottom: 16px;

    .header-item {
      display: flex;
      align-items: center;
      color: #969799;

      .header-text {
        font-size: 14px;
        margin-left: 2px;
      }
    }
  }

  .scene-selection {
    margin-bottom: 24px;

    .selection-label {
      color: #323233;
      font-size: 14px;
      margin-bottom: 8px;
    }

    .el-dropdown {
      width: 100%;

      .dropdown-button {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 6px 12px;
        border: 1px solid #e5e6eb;
        border-radius: 4px;
        cursor: pointer;

        &.allDisabled {
          cursor: not-allowed;
          background: #f7f8fa;
        }
      }
    }

    :deep(.selection-popper) {
      width: 100%;
    }
  }

  .task-creation {
    .task-title {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 8px;
      color: #323233;
    }

    .status-item {
      display: flex;
      align-items: center;
      margin-bottom: 4px;
      color: #969799;

      .status-text {
        font-size: 15px;
        margin-left: 2px;
      }
    }

    .processing-status {
      .processing-text {
        font-size: 14px;
        color: #323233;
      }
    }
  }
}
:deep(.el-form-item__label) {
  color: #323233;
}
</style>

<style lang="less">
.record-upload-auto-form {
  .el-form-item {
    display: flex;
    margin-bottom: 12px;
    height: 44px;
    border-radius: 12px;
    background-color: #f7f8fa;
  }
  .el-form-item__error {
    padding-top: 8px;
    left: -70px;
  }
  .isError {
    border: 1px solid #f54e4e !important;
  }
  .el-form-item__label,
  .el-form-item--label-top .el-form-item__label {
    line-height: 44px !important;
    color: #646566 !important;
    margin: 0 12px;
  }
  .el-select .el-select__wrapper {
    box-shadow: none !important;
    background-color: #f7f8fa !important;
    line-height: 44px;
  }
  .el-icon {
    color: #323233 !important;
    // font-size: 12px;
  }
  .customer-select__drawer {
    border: none !important;
    background: #f7f8fa !important;
  }
  .el-form-item__label:after {
    content: none !important;
  }
  .el-slider {
    margin-left: 4px;
  }
  .el-form-item.is-error {
    border: 1px solid #f54e4e !important;
    /* 可选：调整圆角以匹配你的设计 */
    border-radius: 12px;
  }
  // .el-form-item__error {
  //   display: none;
  // }
}
</style>
